import {
  fetchProfileGetProfile,
  fetchProfileGetProfilePicture,
  fetchProfileUpdatePersonalInformation,
  fetchProfileUploadProfilePicture,
  type ProfileDto,
  type ProfileUploadProfilePictureRequestBody,
} from "@crowdcoursing/api/openapi";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { writeAvatarCacheBuster } from "~services/avatar/avatarCacheBuster";
import { assertNonEmptyString } from "~utils/assert";
import { makeProfileAvatarUrl, withProfileAvatarCacheBust } from "~utils/url";

import { errorToast, successToast } from "../state/notifications";
import { type State, type UserPersonalInfo } from "../state/state";

/** @deprecated We are phasing out `redux` */
export const getProfile = createAsyncThunk<ProfileDto, undefined, { state: State }>(
  "user/getProfile",
  async (_, { getState }) => {
    const { user } = getState();
    if (!user) {
      throw new Error("`userId` not found");
    }
    const response = await fetchProfileGetProfile({ pathParams: { userId: user.id } });
    if (!response) {
      throw new Error("Failed to fetch profile");
    }
    return response as unknown as ProfileDto;
  },
);

/** @deprecated We are phasing out `redux` */
export const savePersonalInfo = createAsyncThunk<UserPersonalInfo, UserPersonalInfo, { state: State }>(
  "user/savePersonalInfo",
  async (body, thunkAPI) => {
    const userId = thunkAPI.getState().user?.id;
    assertNonEmptyString(userId);
    await fetchProfileUpdatePersonalInformation({ body, pathParams: { userId } });
    thunkAPI.dispatch(successToast({ message: "Save successful!" }));
    return body;
  },
);

/** @deprecated We are phasing out `redux` */
export const saveProfilePicture = createAsyncThunk<string, File, { state: State }>(
  "user/savePicture",
  async (file, thunkAPI) => {
    const userId = thunkAPI.getState().user?.id;
    assertNonEmptyString(userId);
    const body = new FormData();
    body.append("FormFile", file);
    const timestamp = Date.now();
    try {
      await fetchProfileUploadProfilePicture({
        body: body as unknown as ProfileUploadProfilePictureRequestBody,
        headers: {
          "Content-Type": undefined,
        },
        pathParams: { userId },
      });
      writeAvatarCacheBuster("profile", userId, timestamp);
      const avatarUrl = withProfileAvatarCacheBust(makeProfileAvatarUrl(userId), userId, timestamp);
      thunkAPI.dispatch(successToast({ message: "Profile picture updated!" }));
      return avatarUrl;
    } catch {
      thunkAPI.dispatch(errorToast({ message: "Failed to upload profile picture." }));
      throw new Error("Failed to upload profile picture");
    }
  },
);

/** @deprecated We are phasing out `redux` */
// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
export const deleteProfilePicture = createAsyncThunk<void, void, { state: State }>(
  "user/deletePicture",
  async (_, thunkAPI) => {
    const userId = thunkAPI.getState().user?.id;
    assertNonEmptyString(userId);
    await fetchProfileGetProfilePicture({ pathParams: { userId } });
  },
);
